import type { ReconcilerExecution } from '../../../../types/frontend';
import type { PhaseOutcomeType } from '../../../../types/backend';
import type { StageState, StageStep } from '../../../../shared/components/StageStepper';

// The reconciler's own phase vocabulary. The render lives in the shared
// `StageStepper`; this file only turns an execution into steps for it.
export type PhaseState = StageState;
export type PhaseStep = StageStep & { key: PhaseKey };
export type PhaseKey = 'observe' | 'plan' | 'execute' | 'verify';

// The real per-phase status (from the execution history) drives the render.
const OUTCOME_STATE: Record<PhaseOutcomeType, PhaseState> = {
  succeeded: 'success',
  failed: 'failed',
  running: 'running',
  not_reached: 'skipped',
  unknown: 'unknown',
};

const DEFAULT_NOTE: Record<PhaseState, string> = {
  success: 'completed',
  failed: 'failed',
  running: 'in progress',
  warning: 'drift detected',
  skipped: 'not reached',
  unknown: 'no execution detail',
};

/**
 * Derive per-phase steps from the REAL per-phase outcomes the backend reads from
 * the execution history (`execution.phaseOutcomes`), authoritative even for
 * failed runs. Phase payloads (plan/execute/verify) only ENRICH the note; they
 * never decide whether a phase ran. Only the reconciler's declared phases render.
 */
export function derivePhases(
  execution: ReconcilerExecution,
  declaredPhases?: PhaseKey[],
): PhaseStep[] {
  const { plan, execute, verify, phaseOutcomes } = execution;
  const outcomeOf = (k: PhaseKey): PhaseOutcomeType => phaseOutcomes?.[k] ?? 'unknown';

  const step = (
    key: PhaseKey,
    label: string,
    state: PhaseState,
    note?: string,
    successIcon?: StageStep['successIcon'],
  ): PhaseStep => ({
    key, label, state, note: note || DEFAULT_NOTE[state], successIcon,
  });

  // OBSERVE
  const observeState = OUTCOME_STATE[outcomeOf('observe')];
  // Observe has no "succeeded" semantics of its own; show the eye when it ran.
  const observe = step('observe', 'Observe', observeState,
    observeState === 'success' ? 'read desired + actual state' : undefined, 'observed');

  // PLAN: enrich the success note from the plan payload.
  const planState = OUTCOME_STATE[outcomeOf('plan')];
  let planNote: string | undefined;
  if (planState === 'success' && plan) {
    planNote = plan.no_change
      ? (plan.summary || plan.reason || 'no changes, desired == actual')
      : `${plan.groups.reduce((s, g) => s + g.actions.length, 0)} action${
          plan.groups.reduce((s, g) => s + g.actions.length, 0) !== 1 ? 's' : ''
        } planned`;
  }
  const planStep = step('plan', 'Plan', planState, planNote);

  // EXECUTE: enrich from the execute payload + the run error.
  const executeState = OUTCOME_STATE[outcomeOf('execute')];
  let executeNote: string | undefined;
  if (execute) {
    const failed = execute.groups.reduce(
      (s, g) => s + g.actions.filter((a) => a.status === 'failed').length, 0);
    const applied = execute.groups.reduce((s, g) => s + g.actions.length, 0);
    if (executeState === 'failed') {
      executeNote = execute.error || execution.error
        || `${failed} action${failed !== 1 ? 's' : ''} failed`;
    } else if (executeState === 'success') {
      executeNote = `${applied} action${applied !== 1 ? 's' : ''} applied`;
    }
  } else if (executeState === 'failed') {
    executeNote = execution.error || 'action failed';
  }
  const executeStep = step('execute', 'Execute', executeState, executeNote);

  // VERIFY: a succeeded verify that observed drift is a warning, not a clean pass;
  // one still settling in AWS is in progress, neither drift nor a clean pass.
  let verifyState = OUTCOME_STATE[outcomeOf('verify')];
  let verifyNote: string | undefined;
  if (verifyState === 'success' && verify) {
    if (verify.outcome === 'CONVERGED') {
      verifyNote = verify.summary || 'confirmed in AWS';
    } else if (verify.outcome === 'SETTLING') {
      verifyState = 'running';
      verifyNote = verify.summary || 'settling in AWS';
    } else {
      verifyState = 'warning';
      const driftCount = verify.observations.length;
      verifyNote = verify.summary
        || (driftCount > 0
          ? `drift on ${driftCount} resource${driftCount !== 1 ? 's' : ''}`
          : 'drift detected');
    }
  }
  const verifyStep = step('verify', 'Verify', verifyState, verifyNote);

  const all: PhaseStep[] = [observe, planStep, executeStep, verifyStep];
  if (!declaredPhases) return all;
  const declared = new Set(declaredPhases);
  return all.filter((p) => declared.has(p.key));
}
